import { useState } from "react";
import { Sparkles, Clock, Users, CheckCircle, Download, Edit3, Save, Target, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const defaultContent = {
  brandName: "Thrive with Deepa",
  tagline: "Leadership & Career Coaching for Mid-Career Professionals",
  
  // Section 1: Complimentary Call
  section1Badge: "Complimentary Offer",
  section1Title: "45-Minute Private",
  section1TitleHighlight: "Coaching Clarity Call",
  section1Subtitle: "For Mid-Career Professionals Ready to Transform",
  section1Benefits: [
    "Clarity on the challenges holding you back",
    "Insights into your leadership blind spots",
    "Actionable next steps tailored to your goals",
    "A safe space to explore your career journey",
  ],
  section1CTA: "Apply Now at:",
  section1Link: "deepa-coaching.com/#clarity-call",
  
  // Section 2: Career Wheel
  section2Badge: "Free Self-Assessment",
  section2Title: "The Career Wheel",
  section2Subtitle: "Visualize Your Career Satisfaction",
  section2Benefits: [
    "Assess 9 key dimensions of your career",
    "Get a visual radar chart of your satisfaction",
    "Identify priority areas for growth",
    "Receive personalized insights from Deepa",
  ],
  section2CTA: "Request Access at:",
  section2Link: "deepa-coaching.com/#career-wheel",
  
  // Credentials
  credentials: "Deepa Prashanth — Leadership & Career Coach | 275+ Coaching Hours | ICF Level 1 Trained | PROSCI Certified",
  contact: "bookings@deepa-coaching.com",
};

const Poster = () => {
  const [content, setContent] = useState(defaultContent);
  const [isEditing, setIsEditing] = useState(false);

  const handleDownload = () => {
    window.print();
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const updateField = (field, value) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const updateBenefit = (section, index, value) => {
    const key = section === 1 ? "section1Benefits" : "section2Benefits";
    setContent((prev) => ({
      ...prev,
      [key]: prev[key].map((b, i) => (i === index ? value : b)),
    }));
  };

  const EditableText = ({ field, className, tag = "span" }) => {
    const Tag = tag;
    if (isEditing) {
      return (
        <Tag
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => updateField(field, e.target.textContent)}
          className={`${className} outline-2 outline-dashed outline-primary/50 outline-offset-2 focus:outline-primary`}
        >
          {content[field]}
        </Tag>
      );
    }
    return <Tag className={className}>{content[field]}</Tag>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-primary/10 py-8 px-4 print:p-0 print:bg-white">
      {/* Action Buttons - hidden in print */}
      <div className="max-w-3xl mx-auto mb-6 flex flex-wrap justify-center gap-3 print:hidden">
        <Button
          onClick={toggleEdit}
          data-testid="edit-poster-btn"
          className={isEditing ? "bg-green-600 hover:bg-green-700 text-white" : "bg-primary text-white hover:bg-primary/90"}
        >
          {isEditing ? (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          ) : (
            <>
              <Edit3 className="mr-2 h-4 w-4" />
              Edit Poster
            </>
          )}
        </Button>
        <Button
          onClick={handleDownload}
          data-testid="download-poster-btn"
          className="bg-primary text-white hover:bg-primary/90"
        >
          <Download className="mr-2 h-4 w-4" />
          Print / Save as PDF
        </Button>
        <Button
          onClick={() => (window.location.href = "/")}
          variant="outline"
          data-testid="back-home-btn"
        >
          Back to Home
        </Button>
      </div>

      {isEditing && (
        <div className="max-w-3xl mx-auto mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-center text-sm print:hidden">
          ✏️ <strong>Editing Mode:</strong> Click any text to edit it. Click "Save Changes" when done.
        </div>
      )}

      {/* Poster */}
      <div 
        className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none"
        data-testid="poster"
      >
        {/* Header Banner */}
        <div 
          className="relative h-40 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1768744781410-757053db2ab0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/70" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <EditableText
              field="brandName"
              tag="h1"
              className="text-4xl md:text-5xl font-bold text-white"
              style={{ 
                fontFamily: "'Dancing Script', cursive",
                textShadow: "2px 2px 4px rgba(0,0,0,0.4)"
              }}
            />
            <EditableText
              field="tagline"
              tag="p"
              className="text-sm md:text-base text-white/95 mt-1 font-medium"
              style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}
            />
          </div>
        </div>

        {/* SECTION 1: Complimentary Call */}
        <div className="p-8 md:p-12 border-b-4 border-dashed border-primary/20">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent/20 border-2 border-accent/40 rounded-full">
              <Sparkles className="h-4 w-4 text-accent-foreground" />
              <EditableText
                field="section1Badge"
                className="text-sm font-bold uppercase tracking-wider text-primary"
              />
            </div>
          </div>

          <div className="text-center mb-6">
            <EditableText
              field="section1Title"
              tag="h2"
              className="text-3xl md:text-4xl font-bold tracking-tight mb-2 block"
            />
            <EditableText
              field="section1TitleHighlight"
              tag="h2"
              className="text-3xl md:text-4xl font-bold tracking-tight text-primary block"
            />
            <EditableText
              field="section1Subtitle"
              tag="p"
              className="text-base text-muted-foreground italic mt-3"
            />
          </div>

          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 mb-6">
            <h3 className="text-base font-bold mb-4 text-center flex items-center justify-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              What You'll Walk Away With:
            </h3>
            <div className="space-y-2">
              {content.section1Benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  {isEditing ? (
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateBenefit(1, i, e.target.textContent)}
                      className="text-sm outline-2 outline-dashed outline-primary/50 outline-offset-2"
                    >
                      {benefit}
                    </span>
                  ) : (
                    <span className="text-sm">{benefit}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="text-center p-3 bg-primary/5 rounded-xl">
              <Clock className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-xl font-bold text-primary">45 min</p>
              <p className="text-xs text-muted-foreground">Focused Session</p>
            </div>
            <div className="text-center p-3 bg-primary/5 rounded-xl">
              <Users className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-xl font-bold text-primary">1:1</p>
              <p className="text-xs text-muted-foreground">Private</p>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-block bg-primary text-white px-6 py-3 rounded-full font-bold text-base mb-3">
              Apply Now
            </div>
            <EditableText field="section1CTA" tag="p" className="text-xs text-muted-foreground mb-1" />
            <a 
              href={`https://${content.section1Link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-bold text-primary hover:underline break-all"
            >
              {content.section1Link}
            </a>
          </div>
        </div>

        {/* SECTION 2: Career Wheel */}
        <div className="p-8 md:p-12">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary/10 border-2 border-primary/30 rounded-full">
              <TrendingUp className="h-4 w-4 text-primary" />
              <EditableText
                field="section2Badge"
                className="text-sm font-bold uppercase tracking-wider text-primary"
              />
            </div>
          </div>

          <div className="text-center mb-6">
            <EditableText
              field="section2Title"
              tag="h2"
              className="text-3xl md:text-4xl font-bold tracking-tight text-primary mb-2 block"
            />
            <EditableText
              field="section2Subtitle"
              tag="p"
              className="text-base text-muted-foreground italic"
            />
          </div>

          <div className="bg-gradient-to-br from-accent/5 to-primary/5 rounded-2xl p-6 mb-6">
            <h3 className="text-base font-bold mb-4 text-center flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Benefits of the Career Wheel:
            </h3>
            <div className="space-y-2">
              {content.section2Benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  {isEditing ? (
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateBenefit(2, i, e.target.textContent)}
                      className="text-sm outline-2 outline-dashed outline-primary/50 outline-offset-2"
                    >
                      {benefit}
                    </span>
                  ) : (
                    <span className="text-sm">{benefit}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6 text-center">
            <p className="text-sm font-semibold text-primary mb-1">
              🔐 Access Code Required
            </p>
            <p className="text-xs text-muted-foreground">
              Request your unique access code to unlock this exclusive assessment
            </p>
          </div>

          <div className="text-center">
            <div className="inline-block bg-primary text-white px-6 py-3 rounded-full font-bold text-base mb-3">
              Request Access
            </div>
            <EditableText field="section2CTA" tag="p" className="text-xs text-muted-foreground mb-1" />
            <a 
              href={`https://${content.section2Link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-bold text-primary hover:underline break-all"
            >
              {content.section2Link}
            </a>
          </div>
        </div>

        {/* Credentials Footer */}
        <div className="border-t border-border/50 p-6 bg-primary/5">
          <EditableText
            field="credentials"
            tag="p"
            className="text-xs text-center text-muted-foreground leading-relaxed"
          />
          <p className="text-center text-sm font-semibold text-primary mt-3">
            📧 <EditableText field="contact" />
          </p>
        </div>

        {/* Brand Footer */}
        <div className="bg-primary py-4 text-center">
          <p 
            className="text-3xl font-bold text-white"
            style={{ fontFamily: "'Dancing Script', cursive" }}
          >
            {content.brandName}
          </p>
        </div>
      </div>

      {/* Instructions - hidden in print */}
      <div className="max-w-3xl mx-auto mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm print:hidden">
        <h4 className="font-bold mb-2">📱 How to Share:</h4>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>Click <strong>Edit Poster</strong> to customize text (changes are temporary)</li>
          <li>Click <strong>Print / Save as PDF</strong> to save as an image or PDF</li>
          <li>Screenshot on your phone to share on WhatsApp/Instagram</li>
          <li>All links in the poster are clickable when viewed digitally</li>
        </ul>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          @page { margin: 0; size: A4; }
        }
      `}</style>
    </div>
  );
};

export default Poster;
